import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  FolderOpen,
  Sparkles,
  X,
  FileText,
  Layers,
  ArrowUpRight,
} from "lucide-react";
import { getAllMenuModulePathAndLogo } from "../../utils/MenuModulePathAndLogoListOfThisProduct";
import ChatAssistantModal from "./ChatAssistantModal";
import canUserSeeThis from "../common/CheckCanUserSeeThis";
import "../../css/Home.css";

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="relative w-16 h-16">
      <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
      <div className="absolute inset-0 border-4 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
    <p className="mt-4 text-slate-500 font-medium">Loading Workspace...</p>
  </div>
);

function Home() {
  const navigate = useNavigate();
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [currentSubmodules, setCurrentSubmodules] = useState([]);

  const {
    currentSelectedMouleObject,
    groupedModuleSubmoduleMain,
    loadinggetAllMenuOfThisRole,
  } = useOutletContext();
  const menu_list = getAllMenuModulePathAndLogo();
  const prevRoute = sessionStorage.getItem("previousRoute") || "";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : [];

  useEffect(() => {
    if (groupedModuleSubmoduleMain && currentSelectedMouleObject?.MODULEID) {
      const matchedModule = groupedModuleSubmoduleMain.find(
        (item) => item.MODULEID === currentSelectedMouleObject.MODULEID,
      );
      setCurrentSubmodules(matchedModule?.SUBMODULES || []);
    }
  }, [groupedModuleSubmoduleMain, currentSelectedMouleObject]);

  return (
    <div className="  px-4 py-8 md:px-6">
      {loadinggetAllMenuOfThisRole ? (
        <LoadingSpinner />
      ) : currentSubmodules.length > 0 ? (
        <div className="max-w-[1500px] mx-auto">
          {/* Section Heading */}
          <div className="mb-6 px-2">
            <h1 className="text-2xl font-bold text-slate-900 capitalize">
              {currentSelectedMouleObject?.MODULENAME}
            </h1>
          </div>

          {/* Submodule Bento Grid */}
          <div className="columns-1 lg:columns-2 xl:columns-3 gap-8 space-y-8">
            {" "}
            {currentSubmodules.map((submodule, idx) => (
              <div
                key={submodule.SUBMODULEID}
                className="bg-white rounded-[2rem] border border-slate-200/60 shadow-sm overflow-hidden flex flex-col"
              >
                <div className="p-4 border-b border-slate-50 bg-slate-50/30">
                  <div className="flex items-center justify-between gap-4">
                    {/* Left Side: Icon and Title Group */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0 p-2.5 bg-white rounded-xl shadow-sm border border-slate-100 text-blue-600">
                        <Layers size={18} />
                      </div>
                      <h3 className="text-base font-bold text-slate-800 tracking-tight truncate">
                        {submodule.SUBMODULENAME}
                      </h3>
                    </div>

                    {/* Right Side: Badge */}
                    <span className="shrink-0 text-[10px] font-black text-slate-400 bg-slate-100/80 px-2 py-1 rounded-lg border border-slate-200/50 uppercase tracking-tighter">
                      {submodule.MENUS?.length || 0} Items
                    </span>
                  </div>
                </div>

                {/* Grid Menu Area */}
                <div className="p-4 grid grid-cols-2 gap-3">
                  {submodule.MENUS?.map((itemMenu) => {
                    const matchedMenu = menu_list.find(
                      (m) =>
                        m.menu_id.trim().toUpperCase() ===
                        itemMenu.MENUID?.trim().toUpperCase(),
                    );
                    const isActive =
                      matchedMenu?.url_path.trim().toUpperCase() ===
                      prevRoute.trim().toUpperCase();

                    return (
                      <button
                        key={itemMenu.MENUID}
                        onClick={() =>
                          matchedMenu
                            ? navigate(matchedMenu.url_path)
                            : alert("Path not found")
                        }
                        className={`group relative flex flex-col items-start p-4 rounded-2xl transition-all duration-300 text-left hover:-translate-y-1 ${isActive
                            ? "bg-blue-600 shadow-lg shadow-blue-200 ring-4 ring-blue-50"
                            : "bg-white border border-slate-100 hover:border-blue-200 hover:shadow-md"
                          }`}
                      >
                        <div
                          className={`mb-3 p-2 rounded-lg transition-colors ${isActive
                              ? "bg-white/20 text-white"
                              : "bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white"
                            }`}
                        >
                          <FileText size={18} strokeWidth={2.5} />
                        </div>

                        <span
                          className={`text-xs font-bold leading-tight line-clamp-2 ${isActive ? "text-white" : "text-slate-700"
                            }`}
                        >
                          {itemMenu.MENUNAME}
                        </span>

                        <ArrowUpRight
                          size={14}
                          className={`absolute top-3 right-3 transition-opacity ${isActive
                              ? "text-white opacity-60"
                              : "text-slate-300 opacity-0 group-hover:opacity-100"
                            }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-32 text-slate-400">
          <FolderOpen size={48} strokeWidth={1} className="mb-4" />
          <p className="font-medium">No submodules configured.</p>
        </div>
      )}

      {API_PATHS[0]?.AI_FEATURE === "true" && canUserSeeThis("ZC") && (
        <div className="fixed bottom-10 right-10 z-[999] flex flex-col items-end">
          {isAssistantOpen && (
            <ChatAssistantModal setIsAssistantOpen={setIsAssistantOpen} />
          )}
          {!isAssistantOpen ? (
            <button
              onClick={() => setIsAssistantOpen(true)}
              className="group relative p-[2px] rounded-full overflow-hidden transition-all duration-500 hover:scale-105 active:scale-95 shadow-[0_10px_30px_rgba(236,72,153,0.15)] hover:shadow-cyan-400/40"
            >
              <div className="absolute inset-[-100%] animate-aura bg-[conic-gradient(from_0deg,transparent_0deg,transparent_120deg,#ec4899_180deg,transparent_240deg,#22d3ee_300deg,transparent_360deg)] opacity-100"></div>
              <div className="relative flex items-center gap-4 bg-white px-5 py-3.5 rounded-full transition-colors group-hover:bg-slate-50">
                <div className="relative">
                  <div className="absolute inset-0 bg-red-400 blur-md opacity-30 animate-pulse"></div>
                  <div className="relative bg-gradient-to-tr from-[#f72d4b] via-[#ec4899] to-[#2281ee] p-2 rounded-full shadow-sm">
                    <Sparkles
                      className="w-5 h-5 text-white"
                      strokeWidth={2.5}
                    />
                  </div>
                </div>
                <div className="flex flex-col items-start leading-none mr-2">
                  <span className="text-[9px] uppercase font-black tracking-[0.2em] text-blue-600 mb-0.5">
                    CUBINA
                  </span>
                  <span className="font-bold text-sm text-slate-800 tracking-tight">
                    Ask Assistant
                  </span>
                </div>
              </div>
            </button>
          ) : (
            /* SHOW CLOSE BUTTON WHEN OPEN */
            <button
              onClick={() => setIsAssistantOpen(false)}
              className="p-4 bg-white text-slate-800 rounded-full shadow-2xl border border-slate-200 hover:bg-slate-50 transition-all animate-in fade-in zoom-in"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Home;
